import { OptionsFilter } from '@mantine/core'

export interface TableColumn<T> {
  key: keyof T
  label: string
  visible: boolean
  sortable?: boolean
  filterable?: boolean
  render?: (item: T, key: keyof T) => React.ReactNode
  csvValue?: (item: T, key: keyof T) => string | number | boolean | null | undefined
  align?: 'left' | 'center' | 'right'
  sortFunction?: (a: T, b: T) => number
  type?: 'string' | 'number' | 'boolean' | 'date'
  tooltip?: string
}

export interface FilterableColumn {
  value: string
  label: string
}

export enum TableActions {
  Configure = 'configure',
  DownloadCsv = 'downloadCsv',
  Filter = 'filter',
  Sort = 'sort',
  Paginate = 'paginate',
  SortDirectionChange = 'sortDirectionChange',
}

// Base props shared between both components
interface BaseTableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  onColumnsChange: (columns: TableColumn<T>[]) => void
  configTitle?: string
  hideConfig?: boolean
  hideCsvDownload?: boolean
  csvFileName?: string
  filterField?: keyof T
  onFilterFieldChange?: (field: keyof T | undefined) => void
  search?: string
  onSearchChange?: (search: string) => void
  filteredData?: T[]
  itemsPerPage: number
  currentPage: number
  onPageChange: (page: number) => void
  sortDirection?: 'asc' | 'desc'
  onSortDirectionChange?: (direction: 'asc' | 'desc') => void
  sortField?: keyof T
  onSortFieldChange?: (field: keyof T) => void
}

// GenericTable-specific props
interface GenericTableSpecificProps<T> {
  isLoading?: boolean
  error?: Error | null
  getRowKey: (item: T) => string | number
  emptyMessage?: string
  paginatedData?: T[]
  isDebouncing?: boolean
  stickyHeader?: boolean
  verticalSpacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  horizontalSpacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

// TableActions-specific props
interface TableActionsSpecificProps {
  onDownloadCsv: () => void
  filterableColumns: FilterableColumn[]
  getFilterOptions: () => string[]
  optionsFilter: OptionsFilter
}

// Shared restriction prop
interface RestrictionProps {
  restrict?: TableActions[]
}

// Final interfaces combining base with specific props
export interface GenericTableProps<T> extends BaseTableProps<T>, GenericTableSpecificProps<T>, RestrictionProps {}

export interface TableActionsProps<T> extends BaseTableProps<T>, TableActionsSpecificProps, RestrictionProps {}
